import { useEffect, useId, useRef, useState } from 'react'
import type { KeyboardEvent, MouseEvent, SyntheticEvent } from 'react'

import type { ArtistHit, Appearance, ArtistPageResponse } from '~/api/types'
import { api } from '~/api/client'

import s from './ArtistSheet.module.css'

/**
 * The artist overlay, ported from `_calendar_page.py`'s `openArtist`.
 *
 * One act: their name, the names they have also been billed under, whether the calendar
 * follows them, and every date of theirs it knows about. The dates are the reason the panel
 * exists — a gig sheet answers "who is playing this room" and this answers the question the
 * reader actually arrived with, "when is this band on".
 *
 * Three things the Python did here belong to the calendar around it rather than to the
 * panel, and are the caller's now:
 *
 *  - **The address.** `openArtist` wrote `/a/<id>` with `history.replaceState` on the way in
 *    and `/calendar` on the way out. Which address a view claims is one decision made in one
 *    place; this component reports opening and closing and does not touch the bar.
 *  - **The refetch.** Following an act re-ranks the whole calendar, so the Python reloaded
 *    `/entries` and re-rendered everything. `onStar` owns that.
 *  - **Back to the gig.** `openArtist(artistId, fromEvent)` drew a "← back to the gig"
 *    button when it had been opened from an event sheet. There is no `fromEvent` in this
 *    component's props, so the button is not here; see the port report.
 */

/** The signed-in viewer. Local by request, and matching `Toolbar`'s and `WeekView`'s. */
export interface Me {
  id: number
  name: string
  username: string
  isEditor: boolean
}

/** How much of a festival's name a date row will carry before it is cut. */
const FESTIVAL_CHARS = 18

/**
 * `short` from the Python, unchanged. It breaks at a bill separator where there is one, so
 * the result reads as names rather than as a word sliced in half.
 */
function short(text: string, n: number): string {
  const value = String(text ?? '')
  if (value.length <= n) return value
  const cut = value.slice(0, n)
  const at = cut.lastIndexOf(' + ')
  return `${at > n / 2 ? cut.slice(0, at) : cut.trimEnd()} …`
}

export interface ArtistSheetProps {
  artistId: number
  /**
   * The reader, or null. `POST /favourite` is the editor's alone — the server 403s anybody
   * else, because following an act moves `curation_status` on every gig they headline and
   * that is the editorial calendar. The control is still drawn for everyone, deliberately
   * and as the Python drew it: hiding what writes would hide what an account is for. What
   * `me` decides is narrower — see `follow` below.
   */
  me: Me | null
  onClose(): void
  onOpen(eventId: string): void
  /** The press. The caller posts it, re-ranks the calendar and lives with the answer. */
  onStar(artistId: number, on: boolean): void
}

/**
 * Two rows for one act, folded into one.
 *
 * Three rows for one DJ is the ordinary case rather than a rare one: feeds spell a name the
 * way the promoter typed it, so `¥ØU$UK€ ¥UK1MAT$U`, `Yousuke Yukimatsu` and
 * `Yousuke Yukimatsu (¥ØU$UK€ ¥UK1MAT$U)` arrive as three people. The slugs share no
 * characters, so nothing automatic reconciles them — and until they are one row, "where
 * else are they playing" answers with a fraction of the truth and the same person appears
 * three times on one bill.
 *
 * On *this* act's page, choosing another folds **this one into that one** — the same
 * direction as the event merge panel, which reads "this is a duplicate of…". The row you
 * are looking at is the one that goes.
 */
export function FoldInto({ artistId, name, onDone }: { artistId: number; name: string; onDone(): void }) {
  const [query, setQuery] = useState('')
  const [hits, setHits] = useState<ArtistHit[]>([])
  const [asking, setAsking] = useState<ArtistHit | null>(null)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => {
    window.clearTimeout(timer.current)
    const text = query.trim()
    if (!text) {
      setHits([])
      return
    }
    timer.current = window.setTimeout(() => {
      void api
        .findActs(text)
        // Never this act: folding a row into itself is refused by the server, and offering
        // it is offering a mistake.
        .then(found => setHits(found.artists.filter(a => a.id !== artistId)))
        .catch(() => setHits([]))
    }, 200)
    return () => window.clearTimeout(timer.current)
  }, [query, artistId])

  if (asking) {
    return (
      // No heading of its own. The only caller is the artist page, which already puts
      // "Same act" above this as its section heading — so the words were printed twice, an
      // `h2` immediately followed by an `h3` saying the same thing.
      <div className={s.sec}>
        <p className={s.spin}>
          Fold <b>{name}</b> into <b>{asking.name}</b>? Every date moves across, this
          spelling is kept as theirs, and this row goes.
        </p>
        <div className={s.actions}>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setBusy(true)
              void api
                .absorb(asking.id, artistId)
                .then(onDone)
                .catch((e: unknown) => setFailed(e instanceof Error ? e.message : String(e)))
                .finally(() => setBusy(false))
            }}
          >
            {busy ? 'Folding…' : 'Fold'}
          </button>
          <button type="button" onClick={() => setAsking(null)}>
            Keep both
          </button>
        </div>
        {failed ? <p className={s.spin}>{failed}</p> : null}
      </div>
    )
  }

  return (
    <div className={s.sec}>
      <input
        className={s.find}
        type="search"
        value={query}
        placeholder="This is the same act as…"
        autoComplete="off"
        onChange={e => setQuery(e.target.value)}
      />
      {hits.map(a => (
        <button key={a.id} type="button" className={s.hit} onClick={() => setAsking(a)}>
          <span>{a.name}</span>
          <span className={s.spin}>
            {a.appearances} {a.appearances === 1 ? 'date' : 'dates'}
          </span>
        </button>
      ))}
    </div>
  )
}

export function ArtistSheet({ artistId, me, onClose, onOpen, onStar }: ArtistSheetProps) {
  const dialog = useRef<HTMLDialogElement>(null)
  const closer = useRef<HTMLButtonElement>(null)
  const labelId = useId()

  const [page, setPage] = useState<ArtistPageResponse | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  /**
   * The press, until fresh data replaces it. Null means "nobody has pressed since this act
   * was loaded" and the payload speaks.
   */
  const [pressed, setPressed] = useState<boolean | null>(null)

  useEffect(() => {
    let live = true
    setPage(null)
    setFailed(null)
    setPressed(null)
    api.artist(artistId).then(
      answer => {
        if (live) setPage(answer)
      },
      (error: unknown) => {
        if (live) setFailed(error instanceof Error ? error.message : String(error))
      },
    )
    return () => {
      live = false
    }
  }, [artistId])

  /*
   * A modal, and the two halves of that word the markup cannot supply: focus goes in when
   * it opens and back to whatever opened it when it closes. `showModal` is guarded because
   * jsdom does not implement `<dialog>` — the fallback sets the attribute the browser would
   * have set, which is enough for the tests to see a dialog and for the CSS to match.
   */
  useEffect(() => {
    const element = dialog.current
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    if (element && typeof element.showModal === 'function') element.showModal()
    else element?.setAttribute('open', '')
    closer.current?.focus()
    return () => {
      opener?.focus()
    }
  }, [])

  /**
   * Escape. Handled on `keydown` rather than left to the browser's own close-watcher,
   * because a `<dialog>` that closes itself leaves this component mounted over a dialog
   * that is no longer open — the caller decides when the panel goes, and it has to be told.
   */
  function escape(event: KeyboardEvent<HTMLDialogElement>) {
    if (event.key !== 'Escape') return
    event.preventDefault()
    onClose()
  }

  /** The same, for a close-watcher that never reaches `keydown` — Android's back gesture. */
  function cancel(event: SyntheticEvent<HTMLDialogElement>) {
    event.preventDefault()
    onClose()
  }

  /** A click that lands on the dialog itself landed on the backdrop; anything inside it did not. */
  function backdrop(event: MouseEvent<HTMLDialogElement>) {
    if (event.target === dialog.current) onClose()
  }

  const artist = page?.artist ?? null
  const following = pressed ?? artist?.is_favourite ?? false

  function follow() {
    const on = !following
    /*
     * The mark moves only for a press that can have moved anything. The Python re-rendered
     * from a refetch and its `post` helper returned null on a refusal, so a stranger's
     * press and a non-editor's 403 both left the star exactly as it was; flipping it here
     * for either would show a reader following an act the server never recorded. `onStar`
     * is still called for all of them — that is what sends a signed-out reader to sign in,
     * and what surfaces the 403 to everybody else.
     */
    if (me?.isEditor) setPressed(on)
    onStar(artistId, on)
  }

  /*
   * `aria-modal` is what a real `showModal` sets implicitly. It is said out loud because
   * the fallback above cannot set it, and because a panel that traps the reader should say
   * so whichever path opened it. `aria-labelledby` names the panel with whatever it is
   * currently showing — the act, or the word "loading…" — so it is never a dangling id.
   */
  return (
    <dialog
      ref={dialog}
      className={s.dialog}
      aria-modal="true"
      aria-labelledby={labelId}
      onClick={backdrop}
      onKeyDown={escape}
      onCancel={cancel}
    >
      <button type="button" className={s.close} aria-label="Close" ref={closer} onClick={onClose}>
        ×
      </button>
      <div className={s.sheet}>
        <div className={s.pad}>
          {artist ? (
            <ArtistBody
              artist={artist}
              appearances={page?.appearances ?? []}
              labelId={labelId}
              following={following}
              me={me}
              onOpen={onOpen}
              onFollow={follow}
              onClose={onClose}
            />
          ) : (
            <div className={s.spin} id={labelId}>
              {failed === null ? 'loading…' : `That did not load: ${failed}`}
            </div>
          )}
        </div>
      </div>
    </dialog>
  )
}

export function ArtistBody({
  artist,
  appearances,
  labelId,
  following,
  me,
  onOpen,
  onFollow,
  onClose,
}: {
  artist: ArtistPageResponse['artist']
  appearances: Appearance[]
  labelId: string
  following: boolean
  /** The editor gets the fold control; nobody else does. */
  me: Me | null
  onOpen: (eventId: string) => void
  onFollow: () => void
  onClose: () => void
}) {
  return (
    <>
      <h2 id={labelId}>{artist.name}</h2>

      {/*
        Not in the Python, which printed the name and nothing else. An act is billed under
        more than one name more often than it looks — a DJ's given name beside the one on
        the poster, a band's old spelling — and a reader who searched for one of them and
        landed on the other has no way to tell they are in the right place. See the report.
      */}
      {artist.aliases.length > 0 ? (
        <p className={s.aka}>Also billed as {artist.aliases.join(' · ')}</p>
      ) : null}

      <div className={s.when}>
        {`${artist.upcoming} upcoming ${artist.upcoming === 1 ? 'date' : 'dates'} — ` +
          'headlining, supporting and festival slots alike'}
      </div>

      <div className={s.actions}>
        <button type="button" className={s.heart} aria-pressed={following} onClick={onFollow}>
          {following ? '★ Everything by them is added' : '☆ Add everything by them'}
        </button>
      </div>

      <div className={s.sec}>
        <h3>Playing</h3>
        <div className={s.dates}>
          {appearances.length > 0 ? (
            appearances.map(date => (
              <button
                key={date.id}
                type="button"
                className={s.dline}
                onClick={() => onOpen(date.id)}
              >
                <span className={s.d}>{date.when}</span>
                <span className={s.where}>{date.venue}</span>
                <span className={s.r}>
                  {/* As on the act's own page and on a bill: only where a source said so. */}
                  {date.role_stated ? date.role : ''}
                  {date.festival ? ` · ${short(date.festival, FESTIVAL_CHARS)}` : ''}
                </span>
              </button>
            ))
          ) : (
            <div className={s.spin}>No upcoming dates.</div>
          )}
        </div>
      </div>

      {/* The editor's, like every other control that changes what everybody sees. */}
      {me?.isEditor ? (
        <FoldInto artistId={artist.id} name={artist.name} onDone={onClose} />
      ) : null}
    </>
  )
}
